import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bookingServicesApi,
  type CreateBookingServicePayload,
  type UpdateBookingServicePayload,
} from '../api/bookingServicesApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'booking-services';

/**
 * List the bookable services for the given tenant. Scoping happens server-side
 * via the x-tenant-id header (set from selectedTenantId by ApiFetch); the
 * tenantId is part of the query key so the cache stays per-tenant, and the
 * query is disabled until a tenant is selected.
 */
export const useBookingServices = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, tenantId],
    queryFn: () => bookingServicesApi.list(),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

export const useCreateBookingService = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateBookingServicePayload) => bookingServicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Booking service created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create booking service')),
  });
};

export const useUpdateBookingService = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingServicePayload }) =>
      bookingServicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Booking service updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update booking service')),
  });
};
